import {handleActions} from 'redux-actions';
import {Record, List} from 'immutable';
import moment from 'moment-timezone';

import * as Actions from './actions';

const Period = Record({
    from: String(),
    to: String(),
});

const Week = Record({
    days: List(),
    title: String(),
});

const Day = Record({
    day: String(),
    // eslint-disable-next-line camelcase
    day_localdate: String(),
    weekday: String(),
    monthDay: String(),
    note: String(),
    from: null,
    to: null,
    openingTime: null,
    closingTime: null,
    allowedFrom: null,
    allowedTo: null,
    isPreciseRSVPEnabled: null,
});

export const Vacation = Record({
    institutionId: String(),
    title: String(),
    body: String(),
    deadline: String(),
    period: new Period(),
    weeks: List(),
    recipientId: String(),
    askForDetailedAttendance: false,
});

/*
    If no opening hours for a given day is found, default to these values
    Signed off by HMR
 */
const defaultOpening = 7 * 60; // 07:00
const defaultClosing = 17 * 60; // 17:00

const dayFromApi = (day, openingHours, allowedHours, isPreciseRSVPEnabled) => {
    const momentDay = moment(day.day_localdate, 'YYYY-MM-DD');
    const opening = openingHours && openingHours[momentDay.day() - 1];
    const allowedAndOpen = allowedHours.filter(day => day.open);
    const allowed = allowedAndOpen.find(e => e.date === day.day_localdate);

    let to;
    let from;

    if (isPreciseRSVPEnabled) {
        to = allowed.toHour * 60 + allowed.toMinute;
        from = allowed.fromHour * 60 + allowed.fromMinute;
    } else if (opening) {
        to = opening.toHour * 60 + opening.toMinute;
        from = opening.fromHour * 60 + opening.fromMinute;
    } else {
        to = defaultClosing;
        from = defaultOpening;
    }

    return new Day({
        day: day.day,
        // eslint-disable-next-line camelcase
        day_localdate: day.day_localdate,
        weekday: momentDay.format('dddd'), // Friday
        monthDay: momentDay.format('LL'), // 9th Feb
        note: day.note,
        from,
        to,
        openingTime: opening ? opening.fromHour * 60 + opening.fromMinute : defaultOpening,
        closingTime: opening ? opening.toHour * 60 + opening.toMinute : defaultClosing,
        allowedFrom: allowed ? allowed.fromHour * 60 + allowed.fromMinute : defaultOpening,
        allowedTo: allowed ? allowed.toHour * 60 + allowed.toMinute : defaultClosing,
        isPreciseRSVPEnabled,
    });
};

const weekFromApi = (week, openingHours, allowedHours, isPreciseRSVPEnabled) => {
    const filteredDays = isPreciseRSVPEnabled
        ? week.days.filter(day => {
              const allowedAndOpen = allowedHours.filter(day => day.open);
              const allowed = allowedAndOpen.find(e => e.date === day.day_localdate);
              return allowed && allowed.fromHour > 0 && allowed.toHour > 0;
          })
        : week.days;

    return new Week({
        title: week.title,
        days: List(filteredDays).map(day => dayFromApi(day, openingHours, allowedHours, isPreciseRSVPEnabled)),
    });
};

const vacationFromApi = (vacation, openingHours, allowedHours, isPreciseRSVPEnabled) =>
    new Vacation({
        institutionId: vacation.institutionId,
        title: vacation.title,
        body: vacation.body,
        deadline: vacation.deadline_localdate,
        period: new Period({
            form: moment(vacation.period.from_localdate, 'YYYY-MM-DD').format('LL'),
            to: moment(vacation.period.to_localdate, 'YYYY-MM-DD').format('LL'),
        }),
        weeks: List(vacation.days).map(({week}) => weekFromApi(week, openingHours, allowedHours, isPreciseRSVPEnabled)),
        recipientId: vacation.recipients && vacation.recipients.length && vacation.recipients[0].id,
        askForDetailedAttendance: vacation.askForDetailedAttendance,
    });

const State = Record({
    loading: false,
    updating: false,
    replies: List(),
    vacation: new Vacation(),
    collapsedRows: List(), // List of weeks that should be rendered as collapsed

    /**
     * openingHours: Array<{
     *  day: number;
     *  fromHour: number;
     *  fromMinute: number;
     *  open: boolean;
     *  toHour: number;
     *  toMinute: number;
     * }>
     */
    openingHours: [],
    allowedHours: [],
    isPreciseRSVPEnabled: false,
});

const findAnswerInReplies = (day, dayReplies, answer) =>
    dayReplies.some(reply => reply.day_localdate === day.day_localdate && reply.answer === answer);

const shouldCollapseWeek = (week, replies) => {
    if (week.days.every(day => replies.every(reply => findAnswerInReplies(day, reply.reply.dayReplies, 1)))) {
        let from = null;
        let to = null;
        return week.days.every(day =>
            replies.every(({reply}) =>
                reply.dayReplies.find(dayReply => {
                    if (dayReply.day_localdate !== day.day_localdate) {
                        return false;
                    }
                    if (!from || !to) {
                        from = dayReply.from;
                        to = dayReply.to;
                    }
                    return dayReply.from === from && dayReply.to === to;
                }),
            ),
        );
    } else {
        return week.days.every(day => replies.every(reply => findAnswerInReplies(day, reply.reply.dayReplies, 2)));
    }
};

export default handleActions(
    {
        [Actions.fetch.type]: () =>
            new State({
                loading: true,
            }),
        [Actions.fetchSuccess.type]: (state, {payload}) => {
            const replies = List(payload.replies);
            const vacation = vacationFromApi(
                payload.vacation,
                payload.openingHours,
                payload.allowedHours,
                payload.isPreciseRSVPEnabled,
            );
            let collapsedRows = vacation.weeks.map((_, i) => Boolean(i));

            // Collapsed logic:
            //  1. If no reply has been submitted, collapse all weeks after the first
            //  2. If reply has been submitted, collapse all weeks that are either fully checked or have the same time

            const hasReplied = replies.size && Boolean(replies.last().reply.repliedAt);
            if (hasReplied) {
                collapsedRows = vacation.weeks.map(week => shouldCollapseWeek(week, replies));
            }

            return state.merge({
                loading: false,
                vacation,
                replies,
                collapsedRows,
                openingHours: payload.openingHours,
                allowedHours: payload.allowedHours,
                isPreciseRSVPEnabled: payload.isPreciseRSVPEnabled,
            });
        },
        [Actions.fetchFailed.type]: state =>
            state.merge({
                loading: false,
            }),
        [Actions.update.type]: state =>
            state.merge({
                updating: true,
            }),
        [Actions.updateSuccess.type]: state =>
            state.merge({
                updating: false,
            }),
        [Actions.updateFailed.type]: state =>
            state.merge({
                updating: false,
            }),
        [Actions.toggleRow.type]: (state, {payload}) =>
            state.merge({
                collapsedRows: state.collapsedRows.update(payload.index, val => !val),
            }),
    },
    new State(),
);
